import { NodeUpdateType } from 'three/tsl'
import { EventNode } from 'three/webgpu'

/**
 * Restore the pre-upload timing of r185's instanced-buffer synchronization.
 *
 * Three.js r185 migrated `InstanceNode` to a TSL function and accidentally
 * registered its update-range synchronization with `OnFrameUpdate`. The
 * renderer runs frame updates after geometry upload, so a newly dirty range
 * arrives one frame late. Upstream corrected this by using
 * `OnBeforeFrameUpdate` in mrdoob/three.js#34162.
 *
 * TSL function bodies are built after material setup, so the internal event is
 * not available to capture synchronously. Identify the r185 callback by its
 * duplicated matrix/color buffer signature (`clearUpdateRanges`,
 * `updateRanges.push`, and `version`) and move only that event. Property names
 * survive normal bundler minification; unrelated user-authored
 * `OnFrameUpdate` events retain their normal frame phase.
 *
 * @internal
 */

interface RuntimeEventNode {
  eventType: string
  callback: (...args: unknown[]) => unknown
  updateType: string
  updateBeforeType: string
  getUpdateType: (this: RuntimeEventNode) => string
  getUpdateBeforeType: (this: RuntimeEventNode) => string
  update: (this: RuntimeEventNode, frame: RuntimeNodeFrame) => void
  updateBefore: (this: RuntimeEventNode, frame: RuntimeNodeFrame) => void
}

interface RuntimeNodeFrame {
  frameId?: number
}

type EventNodePrototype = RuntimeEventNode & Record<string, unknown>

const PATCH_FLAG = '__instanceEventPhaseSplitPatched__'
const classifications = new WeakMap<object, boolean>()
const beforeFrameIds = new WeakMap<object, number>()

function hasAtLeastOccurrences(source: string, token: string, minimum: number): boolean {
  let count = 0
  let offset = 0

  while ((offset = source.indexOf(token, offset)) !== -1) {
    count++
    if (count >= minimum) return true
    offset += token.length
  }

  return false
}

/** @internal Exported so the installed Three runtime artifact can guard this fingerprint in CI. */
export function matchesInstanceBufferSyncCallbackSource(callbackSource: string): boolean {
  return (
    hasAtLeastOccurrences(callbackSource, 'clearUpdateRanges', 2) &&
    hasAtLeastOccurrences(callbackSource, 'updateRanges.push', 2) &&
    hasAtLeastOccurrences(callbackSource, '.version', 6)
  )
}

function isInstanceBufferSyncEvent(event: RuntimeEventNode): boolean {
  if (event.eventType !== EventNode.FRAME) return false

  const cached = classifications.get(event)
  if (cached !== undefined) return cached

  const callbackSource = Function.prototype.toString.call(event.callback)
  const isSyncEvent = matchesInstanceBufferSyncCallbackSource(callbackSource)
  classifications.set(event, isSyncEvent)
  return isSyncEvent
}

export function installInstanceEventUpdateBeforePatch(): void {
  const proto = EventNode.prototype as unknown as EventNodePrototype
  if (!proto[PATCH_FLAG]) {
    proto[PATCH_FLAG] = true
    const originalGetUpdateBeforeType = proto.getUpdateBeforeType
    const originalUpdate = proto.update
    const originalUpdateBefore = proto.updateBefore

    proto.getUpdateBeforeType = function (this: RuntimeEventNode) {
      return isInstanceBufferSyncEvent(this) ? NodeUpdateType.FRAME : originalGetUpdateBeforeType.call(this)
    }

    proto.updateBefore = function (this: RuntimeEventNode, frame: RuntimeNodeFrame) {
      if (isInstanceBufferSyncEvent(this) && frame.frameId !== undefined) {
        beforeFrameIds.set(this, frame.frameId)
      }
      originalUpdateBefore.call(this, frame)
    }

    proto.update = function (this: RuntimeEventNode, frame: RuntimeNodeFrame) {
      if (
        isInstanceBufferSyncEvent(this) &&
        frame.frameId !== undefined &&
        beforeFrameIds.get(this) === frame.frameId
      ) {
        return
      }
      originalUpdate.call(this, frame)
    }
  }
}
